import { AssessmentItem, POIItem, POIItemMirror } from '../../model/ManagePOIListItem';
import ServiceEndpoints from "../ServiceEndpoints"
import { EIOPAError } from "../../model/EIOPAError";
import { diagnoseErrorResponse, diagnoseFetchRejection } from "../RequestDiagnostics";

// Extracts the filename from a Content-Disposition header, e.g.
//   attachment; filename="POI_Data_BOG-GR_2026-07-02_87264rows.csv"
// Handles both plain filename= and RFC 5987 filename*=UTF-8''... forms.
function parseFilenameFromContentDisposition(header: string | null): string | undefined {
    if (!header) return undefined;
    const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
        } catch {
            // fall through to the plain form
        }
    }
    const plain = header.match(/filename="?([^";]+)"?/i);
    return plain?.[1]?.trim();
}

export interface DownloadMetadata {
    // Real server filename: POI_Data_<nca>_<date>_<count>rows.csv
    filename: string;
    // Exact rows the CSV will contain, parsed out of the filename. NaN if the
    // filename could not be parsed (e.g. Content-Disposition missing/blocked).
    recordCount: number;
}

// Fetches ONLY the download metadata (server-side COUNT query, no CSV body) via the
// backend's ?filenameOnly=true mode. This is the "gesture 1" prefetch: call it when the
// confirmation dialog OPENS, not inside the "Continue" click that opens the file picker.
// Its result is cached so downloadAllUploadedData can pass the real filename to
// showSaveFilePicker WITHOUT any await preceding the picker in the gesture-2 handler
// (which would expire the browser's transient user activation).
export async function fetchDownloadMetadata(userToken?: string): Promise<DownloadMetadata> {
    const res = await fetch(ServiceEndpoints.DownloadAllUploadedData + "?filenameOnly=true", {
        method: 'get',
        headers: { "Authorization": "Bearer " + userToken },
    });
    if (!res.ok) {
        throw new Error("Failed to fetch download metadata");
    }
    const filename =
        parseFilenameFromContentDisposition(res.headers.get("Content-Disposition")) ??
        `POI_Data_${new Date().toISOString().slice(0, 10)}.csv`;
    const countMatch = filename.match(/_(\d+)rows\.csv$/i);
    const recordCount = countMatch ? parseInt(countMatch[1], 10) : NaN;
    return { filename, recordCount };
}

export async function downloadAllUploadedData(
    userToken?: string,
    prefetchedFilename?: string,
): Promise<void> {
    // File System Access API path: showSaveFilePicker MUST be the first awaited call
    // after the user gesture. Any preceding await — including a prefetch — expires the
    // browser's transient user activation and produces:
    //   "Must be handling a user gesture to show a file picker."
    // To still get the real server filename (NCA code + row count) on the picker, the
    // caller prefetches it via fetchDownloadMetadata on the EARLIER gesture (the dialog
    // open) and passes it here as `prefetchedFilename` — so no await precedes the picker
    // in THIS gesture. If it is absent (prefetch not yet resolved / failed) we fall back
    // to a date-based name and the user can rename in the picker.
    // The fallback blob path below independently gets the real filename via Content-Disposition.
    if ('showSaveFilePicker' in window) {
        const suggestedName = prefetchedFilename ?? `POI_Data_${new Date().toISOString().slice(0, 10)}.csv`;
        let fileHandle: FileSystemFileHandle;
        try {
            fileHandle = await (window as any).showSaveFilePicker({
                suggestedName,
                types: [{ description: 'CSV file', accept: { 'text/csv': ['.csv'] } }],
            });
        } catch (err: unknown) {
            // User cancelled the picker — treat as a silent abort, not an error.
            if (err instanceof DOMException && err.name === 'AbortError') return;
            throw err;
        }
        // Picker accepted. The caller keeps the confirmation modal open and shows the
        // "Downloading…" spinner in it (isDownloading) until this promise resolves.
        // Gesture context is no longer required; fetch and stream into the handle.
        const res = await fetch(ServiceEndpoints.DownloadAllUploadedData, {
            method: 'get',
            headers: { "Authorization": "Bearer " + userToken },
        });
        if (!res.ok || !res.body) {
            throw new Error("Failed to download uploaded data");
        }

        const writable = await fileHandle.createWritable();
        try {
            // pipeTo writes to a temporary swap file; the target file is only
            // committed on writable.close(). If the stream fails mid-transfer we
            // abort so the swap file is discarded and NO partial/truncated CSV is
            // left on disk — the user gets a clean error instead of a bad file.
            await res.body.pipeTo(writable, { preventAbort: false });
        } catch (err: unknown) {
            // Ensure the writable is torn down and the swap file discarded.
            try {
                await writable.abort();
            } catch {
                // ignore — abort can throw if the stream already errored out
            }
            throw new Error(
                "The download did not complete and the file was not saved. Please try again.",
            );
        }
        return;
    }

    // Fallback (Firefox, Safari): fetch first, then buffer as blob and trigger anchor download.
    const res = await fetch(ServiceEndpoints.DownloadAllUploadedData, {
        method: 'get',
        headers: { "Authorization": "Bearer " + userToken },
    });
    if (!res.ok) {
        throw new Error("Failed to download uploaded data");
    }

    // Prefer the server's filename — it embeds the exact record count
    // (POI_Data_<nca>_<date>_<count>rows.csv) so the download can be validated at a glance.
    const filename =
        parseFilenameFromContentDisposition(res.headers.get("Content-Disposition")) ??
        `POI_Data_${new Date().toISOString().slice(0, 10)}.csv`;

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export async function fetchPOI(typeId: number, userToken?: string) {
    return await fetch(ServiceEndpoints.FetchAllPOI + typeId, {
        method: 'get',
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + userToken },
    }).then(async (resp) => {
        const parsedData: POIItem[] = await resp.json();
        return parsedData;
    }).catch((error) => {
        console.error(error);
        return [];
    });
}

export async function fetchPOIById(encryptedId: string, userToken?: string) {
    return await fetch(`${ServiceEndpoints.FetchPOIById}/${encryptedId}`, {
        method: 'get',
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + userToken },
    }).then(async (resp) => {
        const parsedData: POIItem[] = await resp.json();
        return parsedData;
    }).catch((error) => {
        console.error(error);
        return [];
    });
}

export async function checkPOIAssessmentsById(Id: string, userToken?: string) {
    return await fetch(`${ServiceEndpoints.CheckPOIAssessmentsById}/${Id}`, {
        method: 'get',
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + userToken },
    }).then(async (resp) => {
        const parsedData: boolean = await resp.json();
        return parsedData;
    }).catch((error) => {
        console.error(error);
        return false;
    });
}

// Function for updating an POI item
export async function UpdatePOIById(Id: string | undefined, updatedPOI: POIItem, userToken?: string) {
    try {
        const response = await fetch(`${ServiceEndpoints.UpdatePOIById}/${Id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
            },
            // Convert the updatedPOI object to a JSON string and include it in the request body
            body: JSON.stringify(updatedPOI),
        });

        if (response.ok) {
            // If the update is successful, parse and return the response data (if any)
            const parsedData: any = await response.json();
            return parsedData;
        } else {
            // If there is an error response from the server, throw an error
            // throw new Error('Error updating Person Of Interest data.');

            const errorMessage = await response.text();
            return errorMessage
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export async function CreatePersonOfInterest(createdPOI: POIItem, userToken?: string) {
    return await fetch(ServiceEndpoints.CreatePOI, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
        },
        // Convert the updatedPOI object to a JSON string and include it in the request body
        body: JSON.stringify(createdPOI),
    }).then(async (resp) => {
        if (resp.ok) {
            const parsedData: any = await resp.json();
            return parsedData;
        }
        else {
            // If there is an error response from the server, handle it
            // Clone the response before attempting to read it to avoid "body stream already read" error
            const clonedResp = resp.clone();

            try {
                const errorData = await resp.json();
                throw new Error(errorData.errors[0] ?? 'poicreateexist');
            } catch (jsonError) {
                // Non-JSON error body: classify it so WAF/network/auth blocks are self-diagnosing
                // instead of surfacing as the generic POIDefaultErrorMessage.
                const errorMessage = await safeText(clonedResp);
                const diag = diagnoseErrorResponse(resp, errorMessage, ServiceEndpoints.CreatePOI, { tokenSent: !!userToken });
                // For a real WAF/security block we return an actionable message; otherwise keep the
                // original behaviour of returning the raw body (the dialog maps known app strings).
                if (diag.category === 'waf-blocked' || diag.category === 'network-or-blocked' || diag.category === 'auth') {
                    return diag.userMessage;
                }
                return errorMessage || 'Error creating Person Of Interest: Failed to read response';
            }
        }
    }).catch((error) => {
        // fetch rejected outright (offline / CORS / proxy / firewall) — no HTTP response.
        const diag = diagnoseFetchRejection(error, ServiceEndpoints.CreatePOI);
        return diag.userMessage;
    });

}

// Reads a response body as text without throwing (returns '' on failure).
async function safeText(resp: Response): Promise<string> {
    try {
        return await resp.text();
    } catch {
        return '';
    }
}

export async function CreatePOIorFetch(createdPOI: POIItem, userToken?: string) {
    return await fetch(ServiceEndpoints.CreatePOIorFetch, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
        },
        // Convert the updatedPOI object to a JSON string and include it in the request body
        body: JSON.stringify(createdPOI),
    }).then(async (resp) => {
        if (resp.ok) {
            return await resp.json();
        }
        else {
            // If there is an error response from the server, throw an error
            throw new Error('Error creating Person Of Interest data.');
        }
    }).catch((error) => {
        console.error(error);
        return [];
    });

}

// Function for deleting an POI item
export async function DeletePOIById(id: string | undefined, userToken?: string) {
    try {
        // Send a DELETE request to the server's Delete POI endpoint
        const response = await fetch(`${ServiceEndpoints.DeletePOIById}/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
            },
        });
        // Check if the response is successful (HTTP status 200-299)
        if (response.ok) {
            // If the deletion is successful, parse and return the response data (if any)
            return response.json();
        } else {
            // If there is an error response from the server, throw an error
            throw new Error('Error deleting Person Of Interest data.');
        }
    } catch (error) {
        // Handle any errors that occur during the fetch operation
        console.error(error);
        throw error;
    }
}

export async function fetchAssessmentByPoiId(Id: string | undefined, userToken?: string) {
    return await fetch(`${ServiceEndpoints.FetchAssessmentByPoiId}/${Id}`, {
        method: 'get',
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + userToken },
    }).then(async (resp) => {
        const parsedData: AssessmentItem[] = await resp.json();
        return parsedData;
    }).catch((error) => {
        console.error(error);
        return [];
    });
}

// Function for updating an Assessment item
export async function UpdateAssessmentById(Id: string, updatedAssessment: AssessmentItem, userToken?: string) {
    try {
        const response = await fetch(`${ServiceEndpoints.UpdateAssessmentById}/${Id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
            },
            // Convert the updated Assessment object to a JSON string and include it in the request body
            body: JSON.stringify(updatedAssessment),
        });

        if (response.ok) {
            // If the update is successful, parse and return the response data (if any)
            return response.ok;
        } else {
            // if response has a payload, throw an error
            const text = await response.text();
            if (response.status === 400 && text) {
                throw new EIOPAError(text);
            } else {
                // If there is an error response from the server, throw an error
                throw new Error('Error updating Person Of Interest data.');
            }

        }
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export async function CreateAssessment(createdAssessment: AssessmentItem, userToken?: string) {
    try {
        const response = await fetch(ServiceEndpoints.CreateAssessment, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
            },
            // Convert the updatedPOI object to a JSON string and include it in the request body
            body: JSON.stringify(createdAssessment),
        });

        if (response.ok) {
            // If the update is successful, parse and return the response data (if any)
            return response.ok;
        } else {
            // if response has a payload, throw an error
            const text = await response.text();
            if (response.status === 400 && text) {
                throw new EIOPAError(text);
            } else {
                // If there is an error response from the server, throw an error
                throw new Error('Error creating Assessment.');
            }
        }
    } catch (error) {
        console.error(error);
        throw error;
    }
}

// Function for deleting an Assessment item
export async function DeleteAssessmentById(id: number, userToken?: string) {
    try {
        // Send a DELETE request to the server's DeleteAssessment endpoint
        const response = await fetch(`${ServiceEndpoints.DeleteAssessmentById}/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
            },
        });
        // Check if the response is successful (HTTP status 200-299)
        if (response.ok) {
            // If the deletion is successful, parse and return the response data (if any)
            return response.json();
        } else {
            // If there is an error response from the server, throw an error
            throw new Error('Error deleting Assessment data.');
        }
    } catch (error) {
        // Handle any errors that occur during the fetch operation
        console.error(error);
        throw error;
    }
}

export async function ConvertForIndex(poiItem: any, userToken?: string) {
    return await fetch(ServiceEndpoints.ConvertForIndex, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
        },
        // Convert the updatedPOI object to a JSON string and include it in the request body
        body: JSON.stringify(poiItem),
    }).then(async (resp) => {
        if (resp.ok) {
            const parsedData: any = await resp.json();
            return parsedData;
        }
        else {
            // If there is an error response from the server, throw an error
            throw new Error('The POI you are trying to convert is empty');
        }
    }).catch((error) => {
        console.error(error);
        return error;
    });
}

export async function UpdateIndexRecord(poiItem: POIItemMirror, userToken?: string) {
    return await fetch(ServiceEndpoints.UpdateIndexRecord, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json', "Authorization": "Bearer " + userToken,
        },
        // Convert the updatedPOI object to a JSON string and include it in the request body
        body: JSON.stringify(poiItem),
    }).then(async (resp) => {
        if (resp.ok) {
            return resp.ok;
        }
        else {
            // If there is an error response from the server, throw an error
            throw new Error('Error Indexing Person of Interest.');
        }
    }).catch((error) => {
        console.error(error);
        return error;
    });
}
